import { gql, useQuery } from '@apollo/client';
import { Box } from '@ovComponents/1-primative/box/box';
import { Skeleton } from '@ovComponents/1-primative/skeleton/skeleton';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Card } from '@ovComponents/2-component/card/card';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext } from '@providers/userContextProvider';
import { formatMoneyValue, formatPercentValue } from '@utils/moneyFormatting';
import { useContext } from 'react';

type IMarketData = {
  shortName: string;
  ticker: string;
  type: string;
  exchange: string;
  currency: string;
  statistics: IMarketDataStatistics;
};

type IMarketDataStatistics = {
  changeCents: number;
  changePercent: number;
  lastCents: number;
};

const FETCH_MARKET_DATA = gql`
  query FetchMarketDataFromProvider($input: FetchMarketDataFromProviderInput!) {
    fetchMarketDataFromProvider(input: $input) {
      marketData {
        shortName
        ticker
        type
        exchange
        currency
        statistics {
          changeCents
          changePercent
          lastCents
        }
      }
    }
  }
`;

export const MarketDataTicker = ({
  options = {
    tickers: '',
  },
}: {
  options: {
    tickers: string;
  };
}) => {
  const { tickers } = options;
  const { activeOrganization, integrations } = useContext(UserContext);
  const { sys } = useThemeTokens();

  const hasIntegration = integrations.some(
    (integration) =>
      integration.type === 'MARKET_DATA' &&
      integration.provider === 'QUOTE_MEDIA' &&
      integration.organization.id === activeOrganization.id,
  );

  const { data, loading } = useQuery(FETCH_MARKET_DATA, {
    variables: {
      input: {
        organizationId: activeOrganization.id,
        tickers,
      },
    },
    skip: !hasIntegration,
  });

  const findMarketData = (ticker: string): IMarketData =>
    data?.fetchMarketDataFromProvider?.marketData?.find((marketData: any) =>
      marketData.ticker.includes(ticker),
    ) || {
      shortName: ticker,
      ticker,
      exchange: '',
      currency: '',
      statistics: {
        changeCents: 0,
        changePercent: 0,
        lastCents: 0,
      },
    };

  if (!hasIntegration) return null;

  return (
    <Card
      sx={{
        overflowX: 'auto',
        maxHeight: '76px',
        '&::-webkit-scrollbar': {
          height: '4px',
          display: 'none',
        },
        '&:hover::-webkit-scrollbar': {
          display: 'block',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: `${sys.color.onSurfaceVariant}33`,
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: `${sys.color.onSurfaceVariant}4D`,
          borderRadius: '4px',
        },
      }}
    >
      <Box
        display='flex'
        flexDirection='row'
        justifyContent='space-between'
        alignItems='center'
      >
        {tickers.split(',').map((ticker: any) => (
          <Box
            sx={{
              '&:not(:last-of-type)': {
                borderRight: `1px solid ${sys.color.outlineVariant}`,
              },
              p: 2,
            }}
            display='flex'
            flexDirection='column'
            flex={1}
            key={ticker}
            alignItems='start'
            width='100%'
            justifyContent='space-between'
            minWidth={140}
          >
            {loading ? (
              <Skeleton variant='text' width='70%' height='24px' />
            ) : (
              <Typography variant='bodyLarge' weight='bold'>
                {findMarketData(ticker)?.shortName}
              </Typography>
            )}
            {loading ? (
              <Box display='flex' justifyContent='space-between' width='100%'>
                <Skeleton variant='text' width='80px' height='20px' />
                <Skeleton variant='text' width='45px' height='20px' />
              </Box>
            ) : (
              <Box display='flex' justifyContent='space-between' width='100%'>
                <Typography colorVariant='variant' variant='bodyMedium'>
                  {formatMoneyValue(
                    findMarketData(ticker).statistics.lastCents,
                    '$',
                    2,
                    true,
                    findMarketData(ticker).type !== 'INDEX',
                  )}{' '}
                  {findMarketData(ticker).type !== 'INDEX'
                    ? findMarketData(ticker).currency
                    : ''}
                </Typography>
                <Typography
                  variant='bodyMedium'
                  sx={{
                    color:
                      findMarketData(ticker).statistics.changePercent > 0
                        ? sys.color.positive
                        : sys.color.negative,
                  }}
                >
                  {findMarketData(ticker).statistics.changePercent > 0
                    ? '+'
                    : ''}
                  {formatPercentValue(
                    findMarketData(ticker).statistics.changePercent / 100,
                    2,
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Card>
  );
};
